import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import Chatbot from '@/components/shared/Chatbot';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Briefcase, MapPin, IndianRupee, Users, Search, Building2, Send, CheckCircle,
  Clock, Sparkles, Star, TrendingUp, Filter, X, BadgeCheck, Flame, Zap, Calendar,
  GraduationCap, Award, ChevronDown, ChevronRight, ArrowRight, SlidersHorizontal,
  Eye, Bookmark, Share2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PRIMARY = '#0A4DFF';

// ============== Helpers ==============
const isNewJob = (job) => {
  const dateStr = job.posted_at || job.created_at;
  if (!dateStr) return false;
  try {
    const posted = new Date(dateStr);
    const days = (Date.now() - posted.getTime()) / (1000 * 60 * 60 * 24);
    return days <= 7;
  } catch { return false; }
};

const isUrgent = (job) => (parseInt(job.num_requirements) || 0) >= 5;

const formatSalary = (min, max) => {
  if (!min && !max) return 'Not Disclosed';
  if (min && max && min !== max) return `₹${min}–${max} LPA`;
  return `₹${min || max} LPA`;
};

const companyInitials = (name) => {
  if (!name) return 'CO';
  return name.split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
};

const logoColor = (name = '') => {
  const colors = [
    'from-blue-500 to-indigo-600',
    'from-emerald-500 to-teal-600',
    'from-rose-500 to-pink-600',
    'from-amber-500 to-orange-600',
    'from-violet-500 to-purple-600',
    'from-cyan-500 to-sky-600',
  ];
  const hash = [...name].reduce((s, c) => s + c.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

const daysAgo = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    if (days < 30) return `${days} days ago`;
    const months = Math.floor(days / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  } catch { return ''; }
};

// ============== Premium Job Card ==============
const JobCard = ({ job, hasApplied, onApply, onViewDetails, isRecommended, matchScore }) => {
  const newBadge = isNewJob(job);
  const urgent = isUrgent(job);
  const skills = (job.skills || '').split(',').map(s => s.trim()).filter(Boolean);

  return (
    <div
      className="group relative bg-white rounded-2xl border border-slate-200 hover:border-blue-300 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 overflow-hidden flex flex-col"
      data-testid={`job-card-${job.id}`}
    >
      {/* Recommended ribbon */}
      {isRecommended && (
        <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 py-1.5 flex items-center justify-between">
          <span className="text-white text-xs font-semibold flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> AI Recommended for you
          </span>
          {matchScore && (
            <span className="bg-white/25 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {matchScore}% match
            </span>
          )}
        </div>
      )}

      <div className="p-5 flex-1 flex flex-col">
        {/* Header: Logo + Title + Salary */}
        <div className="flex items-start gap-3 mb-3">
          {job.company_logo_url ? (
            <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 overflow-hidden flex items-center justify-center shadow-sm flex-shrink-0 group-hover:shadow-md transition-shadow" data-testid="card-company-logo">
              <img src={job.company_logo_url} alt={job.company_name || 'logo'} loading="lazy" className="w-full h-full object-contain" />
            </div>
          ) : (
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${logoColor(job.company_name)} flex items-center justify-center text-white font-bold text-sm shadow-md flex-shrink-0`} data-testid="card-company-logo">
              {companyInitials(job.company_name)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-bold text-slate-900 text-base leading-tight line-clamp-2 group-hover:text-[#0A4DFF] transition-colors">
                {job.position_name || job.title}
              </h3>
            </div>
            <div className="flex items-center gap-1.5 mt-1">
              <Building2 className="w-3.5 h-3.5 text-slate-400" />
              <p className="text-xs text-slate-600 font-medium truncate">{job.company_name || 'Company'}</p>
              <BadgeCheck className="w-3.5 h-3.5 text-blue-500" title="Verified Employer" />
            </div>
          </div>
        </div>

        {/* Badges Row */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {newBadge && (
            <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-md border border-emerald-200">
              <Zap className="w-3 h-3" /> New
            </span>
          )}
          {urgent && (
            <span className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-md border border-rose-200">
              <Flame className="w-3 h-3" /> Urgent Hiring
            </span>
          )}
          <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-md border border-blue-200">
            <BadgeCheck className="w-3 h-3" /> Verified
          </span>
        </div>

        {/* Meta info grid */}
        <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
          <div className="flex items-center gap-1.5 text-slate-600">
            <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <span className="truncate">{job.location || 'Remote'}</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-600">
            <Briefcase className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <span className="truncate">{job.job_type || 'Full Time'}</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-600">
            <GraduationCap className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <span>{job.experience_min || 0}-{job.experience_max || job.experience_min || 0} yrs</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-600">
            <Users className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <span>{job.num_requirements || 1} {job.num_requirements > 1 ? 'openings' : 'opening'}</span>
          </div>
        </div>

        {/* Salary highlight */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-2.5 mb-3 flex items-center justify-between border border-emerald-100">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-emerald-500 flex items-center justify-center">
              <IndianRupee className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-[10px] text-emerald-600 uppercase font-semibold">Salary</p>
              <p className="text-sm font-bold text-emerald-800 leading-none">
                {formatSalary(job.min_ctc, job.max_ctc)}
              </p>
            </div>
          </div>
          {job.application_deadline && (
            <div className="text-right">
              <p className="text-[10px] text-rose-500 uppercase font-semibold">Apply by</p>
              <p className="text-xs font-semibold text-rose-700">
                {new Date(job.application_deadline).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
              </p>
            </div>
          )}
        </div>

        {/* Skills */}
        {skills.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {skills.slice(0, 4).map((s, i) => (
              <span key={i} className="bg-slate-100 text-slate-700 text-[11px] font-medium px-2 py-0.5 rounded-md">
                {s}
              </span>
            ))}
            {skills.length > 4 && (
              <span className="text-[11px] text-slate-500 px-1 py-0.5">+{skills.length - 4} more</span>
            )}
          </div>
        )}

        <div className="text-[11px] text-slate-400 mb-3 mt-auto">
          Posted {daysAgo(job.posted_at || job.created_at)}
        </div>

        {/* CTA Buttons */}
        <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
          {hasApplied ? (
            <div className="flex-1 flex items-center justify-center gap-2 bg-emerald-50 text-emerald-700 py-2.5 rounded-lg text-sm font-semibold border border-emerald-200">
              <CheckCircle className="w-4 h-4" /> Applied
            </div>
          ) : (
            <Button
              onClick={() => onApply(job)}
              className="flex-1 bg-[#0A4DFF] hover:bg-[#083ddb] text-white rounded-lg h-10 text-sm font-semibold shadow-sm shadow-blue-200"
              data-testid={`apply-btn-${job.id}`}
            >
              <Send className="w-3.5 h-3.5 mr-1.5" /> Apply Now
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => onViewDetails(job)}
            className="rounded-lg h-10 px-3 border-slate-200 hover:bg-slate-50 hover:border-blue-300 text-slate-700 hover:text-blue-600"
            data-testid={`view-details-btn-${job.id}`}
          >
            <Eye className="w-3.5 h-3.5 mr-1" /> Details
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============== Filter Sidebar ==============
const FilterSidebar = ({ filters, setFilters, locations, allSkills, jobTypes, onClear, mobile = false }) => {
  const update = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const toggleSkill = (skill) => {
    setFilters(f => ({
      ...f,
      skills: f.skills.includes(skill) ? f.skills.filter(s => s !== skill) : [...f.skills, skill]
    }));
  };
  const toggleType = (type) => {
    setFilters(f => ({
      ...f,
      jobTypes: f.jobTypes.includes(type) ? f.jobTypes.filter(s => s !== type) : [...f.jobTypes, type]
    }));
  };

  return (
    <aside className={`bg-white rounded-2xl border border-slate-200 p-5 ${mobile ? '' : 'lg:sticky lg:top-24'}`} data-testid="filter-sidebar">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-bold text-slate-900 flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-[#0A4DFF]" /> Filters
        </h3>
        <button onClick={onClear} className="text-xs text-[#0A4DFF] font-semibold hover:underline" data-testid="clear-filters-btn">
          Clear All
        </button>
      </div>

      {/* Location */}
      <div className="mb-5">
        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-2">Location</label>
        <select
          value={filters.location}
          onChange={(e) => update('location', e.target.value)}
          className="w-full h-10 px-3 border border-slate-200 rounded-lg bg-white text-sm focus:border-[#0A4DFF] focus:ring-1 focus:ring-[#0A4DFF] focus:outline-none"
          data-testid="filter-location"
        >
          <option value="">All Locations</option>
          {locations.map(loc => (<option key={loc} value={loc}>{loc}</option>))}
        </select>
      </div>

      {/* Salary Range */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wide">Min Salary</label>
          <span className="text-xs font-bold text-emerald-600">₹{filters.minSalary} LPA+</span>
        </div>
        <input
          type="range" min="0" max="50" step="1"
          value={filters.minSalary}
          onChange={(e) => update('minSalary', Number(e.target.value))}
          className="w-full accent-[#0A4DFF]"
          data-testid="filter-salary"
        />
        <div className="flex justify-between text-[10px] text-slate-400 mt-1">
          <span>₹0</span><span>₹25</span><span>₹50+</span>
        </div>
      </div>

      {/* Experience */}
      <div className="mb-5">
        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-2">Experience</label>
        <select
          value={filters.experience}
          onChange={(e) => update('experience', e.target.value)}
          className="w-full h-10 px-3 border border-slate-200 rounded-lg bg-white text-sm focus:border-[#0A4DFF] focus:ring-1 focus:ring-[#0A4DFF] focus:outline-none"
          data-testid="filter-experience"
        >
          <option value="">Any Experience</option>
          <option value="0-1">Fresher (0-1 yr)</option>
          <option value="1-3">1-3 years</option>
          <option value="3-5">3-5 years</option>
          <option value="5-10">5-10 years</option>
          <option value="10+">10+ years</option>
        </select>
      </div>

      {/* Job Type */}
      <div className="mb-5">
        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-2">Job Type</label>
        <div className="space-y-2">
          {jobTypes.map(type => (
            <label key={type} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 hover:text-[#0A4DFF]">
              <input
                type="checkbox"
                checked={filters.jobTypes.includes(type)}
                onChange={() => toggleType(type)}
                className="w-4 h-4 accent-[#0A4DFF] rounded"
                data-testid={`filter-jobtype-${type.toLowerCase().replace(' ', '-')}`}
              />
              <span>{type}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Skills (top tags) */}
      {allSkills.length > 0 && (
        <div className="mb-2">
          <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-2">Skills</label>
          <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto pr-1">
            {allSkills.slice(0, 20).map(skill => (
              <button
                key={skill}
                onClick={() => toggleSkill(skill)}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-all ${
                  filters.skills.includes(skill)
                    ? 'bg-[#0A4DFF] text-white border-[#0A4DFF]'
                    : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-[#0A4DFF] hover:text-[#0A4DFF]'
                }`}
                data-testid={`filter-skill-${skill.toLowerCase()}`}
              >
                {skill}
              </button>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
};

// ============== Job Details Modal ==============
const JobDetailsModal = ({ job, open, onClose, onApply, hasApplied }) => {
  if (!job) return null;
  const skills = (job.skills || '').split(',').map(s => s.trim()).filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0">
        {/* Hero header */}
        <div className="bg-gradient-to-br from-[#0A4DFF] via-[#1d5bff] to-indigo-700 px-6 py-6 rounded-t-lg text-white">
          <DialogHeader className="space-y-0">
            <div className="flex items-start gap-4">
              {job.company_logo_url ? (
                <div className="w-16 h-16 rounded-xl bg-white border border-white/30 overflow-hidden flex items-center justify-center flex-shrink-0">
                  <img src={job.company_logo_url} alt={job.company_name || 'logo'} className="w-full h-full object-contain" />
                </div>
              ) : (
                <div className={`w-16 h-16 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}>
                  {companyInitials(job.company_name)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-2xl text-white font-bold leading-tight">
                  {job.position_name || job.title}
                </DialogTitle>
                <p className="text-blue-100 text-sm flex items-center gap-1.5 mt-1">
                  <Building2 className="w-4 h-4" /> {job.company_name}
                  <BadgeCheck className="w-4 h-4 ml-1" /> Verified
                </p>
                <div className="flex flex-wrap gap-3 mt-3 text-sm">
                  <span className="flex items-center gap-1 bg-white/15 px-2.5 py-1 rounded-md">
                    <MapPin className="w-3.5 h-3.5" /> {job.location}
                  </span>
                  <span className="flex items-center gap-1 bg-white/15 px-2.5 py-1 rounded-md">
                    <Briefcase className="w-3.5 h-3.5" /> {job.job_type || 'Full Time'}
                  </span>
                  <span className="flex items-center gap-1 bg-white/15 px-2.5 py-1 rounded-md">
                    <Users className="w-3.5 h-3.5" /> {job.num_requirements} openings
                  </span>
                </div>
              </div>
            </div>
            {/* Top Apply CTA */}
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/20">
              {hasApplied ? (
                <div className="flex-1 bg-emerald-500/20 text-white py-2.5 rounded-lg text-center font-semibold flex items-center justify-center gap-2 border border-emerald-300/30">
                  <CheckCircle className="w-4 h-4" /> Already Applied
                </div>
              ) : (
                <Button
                  onClick={() => onApply(job)}
                  className="flex-1 bg-white hover:bg-blue-50 text-[#0A4DFF] font-bold rounded-lg h-11"
                  data-testid="modal-top-apply-btn"
                >
                  <Send className="w-4 h-4 mr-2" /> Apply Now
                </Button>
              )}
            </div>
          </DialogHeader>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6">
          {/* Salary highlight + Deadline */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
              <p className="text-[10px] text-emerald-600 font-bold uppercase">Salary</p>
              <p className="text-base font-bold text-emerald-800 mt-0.5 flex items-center gap-0.5">
                <IndianRupee className="w-3.5 h-3.5" />{formatSalary(job.min_ctc, job.max_ctc).replace('₹', '')}
              </p>
            </div>
            <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
              <p className="text-[10px] text-blue-600 font-bold uppercase">Experience</p>
              <p className="text-base font-bold text-blue-800 mt-0.5">{job.experience_min || 0}-{job.experience_max || job.experience_min || 0} yrs</p>
            </div>
            <div className="bg-violet-50 rounded-xl p-3 border border-violet-100">
              <p className="text-[10px] text-violet-600 font-bold uppercase">Openings</p>
              <p className="text-base font-bold text-violet-800 mt-0.5">{job.num_requirements || 1}</p>
            </div>
            <div className="bg-rose-50 rounded-xl p-3 border border-rose-100">
              <p className="text-[10px] text-rose-600 font-bold uppercase">Apply By</p>
              <p className="text-base font-bold text-rose-800 mt-0.5">
                {job.application_deadline
                  ? new Date(job.application_deadline).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                  : 'Open'}
              </p>
            </div>
          </div>

          {/* Skills */}
          {skills.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-slate-900 mb-2 uppercase tracking-wide">Required Skills</h4>
              <div className="flex flex-wrap gap-2">
                {skills.map((s, i) => (
                  <span key={i} className="bg-blue-50 text-[#0A4DFF] text-xs font-semibold px-3 py-1.5 rounded-full border border-blue-100">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          {job.description && (
            <div>
              <h4 className="text-sm font-bold text-slate-900 mb-2 uppercase tracking-wide flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-[#0A4DFF]" /> Job Description
              </h4>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{job.description}</p>
              </div>
            </div>
          )}

          {/* Requirements / Responsibilities */}
          {job.requirements && (
            <div>
              <h4 className="text-sm font-bold text-slate-900 mb-2 uppercase tracking-wide flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600" /> Requirements & Responsibilities
              </h4>
              <div className="bg-amber-50/40 p-4 rounded-xl border border-amber-100">
                <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{job.requirements}</p>
              </div>
            </div>
          )}

          {/* Company Info */}
          <div>
            <h4 className="text-sm font-bold text-slate-900 mb-2 uppercase tracking-wide flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#0A4DFF]" /> About the Company
            </h4>
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-start gap-3">
              {job.company_logo_url ? (
                <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                  <img src={job.company_logo_url} alt={job.company_name || 'logo'} className="w-full h-full object-contain" />
                </div>
              ) : (
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${logoColor(job.company_name)} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                  {companyInitials(job.company_name)}
                </div>
              )}
              <div>
                <p className="font-bold text-slate-900">{job.company_name}</p>
                <p className="text-xs text-slate-600 mt-0.5 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {job.location}
                </p>
                <p className="text-xs text-slate-500 mt-1">Verified employer on Iqra Skills</p>
              </div>
            </div>
          </div>

          {/* Bottom Apply CTA */}
          <div className="sticky bottom-0 bg-white pt-4 -mx-6 px-6 pb-2 border-t border-slate-200 flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-lg h-11 sm:flex-none border-slate-200 text-slate-700">
              Close
            </Button>
            {hasApplied ? (
              <div className="flex-1 bg-emerald-50 text-emerald-700 py-2.5 rounded-lg text-center font-semibold flex items-center justify-center gap-2 border border-emerald-200">
                <CheckCircle className="w-4 h-4" /> Already Applied
              </div>
            ) : (
              <Button
                onClick={() => onApply(job)}
                className="flex-1 bg-[#0A4DFF] hover:bg-[#083ddb] text-white rounded-lg h-11 font-bold shadow-md shadow-blue-200"
                data-testid="modal-bottom-apply-btn"
              >
                <Send className="w-4 h-4 mr-2" /> Apply Now <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============== Main JobPortal ==============
const JobPortal = () => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [myApplications, setMyApplications] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [studentProfile, setStudentProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [applying, setApplying] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const [filters, setFilters] = useState({
    search: '',
    location: '',
    minSalary: 0,
    experience: '',
    jobTypes: [],
    skills: []
  });

  useEffect(() => {
    fetchJobs();
    if (isAuthenticated && user?.role === 'student') {
      fetchMyApplications();
      fetchRecommendations();
      fetchStudentProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API}/jobs/opportunities`);
      setJobs(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchMyApplications = async () => {
    try {
      const res = await axios.get(`${API}/jobs/my-applications`);
      setMyApplications(res.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchRecommendations = async () => {
    setLoadingRecs(true);
    try {
      const res = await axios.get(`${API}/jobs/ai-recommendations`);
      setRecommendations(res.data?.recommendations || []);
    } catch (e) { console.error(e); }
    finally { setLoadingRecs(false); }
  };

  const fetchStudentProfile = async () => {
    try {
      const res = await axios.get(`${API}/profile/student`);
      setStudentProfile(res.data || null);
    } catch (e) { console.error(e); }
  };

  const hasApplied = (jobId) => myApplications.some(a => a.job_id === jobId);

  const openDetails = (job) => {
    setSelectedJob(job);
    setDetailsOpen(true);
  };

  const openApply = (job) => {
    if (!isAuthenticated) {
      toast.info('Please login to apply');
      navigate('/auth/login');
      return;
    }
    if (user?.role !== 'student') {
      toast.error('Only students can apply to jobs');
      return;
    }
    // Block apply if student profile is incomplete
    if (!studentProfile?.is_complete) {
      toast.error('Pehle apni Profile complete karein, fir job apply kar sakte hain', {
        description: 'Profile update page par redirect kiya jaa raha hai...',
        duration: 4000
      });
      setTimeout(() => navigate('/student/profile'), 800);
      return;
    }
    setSelectedJob(job);
    setCoverLetter('');
    setDetailsOpen(false);
    setApplyOpen(true);
  };

  const submitApply = async () => {
    if (!selectedJob) return;
    setApplying(true);
    try {
      await axios.post(`${API}/jobs/apply`, {
        job_id: selectedJob.id,
        cover_letter: coverLetter
      });
      toast.success('Application submitted successfully!');
      setApplyOpen(false);
      fetchMyApplications();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  const clearFilters = () => setFilters({ search: filters.search, location: '', minSalary: 0, experience: '', jobTypes: [], skills: [] });

  // Derived
  const locations = useMemo(
    () => [...new Set(jobs.map(j => j.location?.split(',')[0]?.trim()).filter(Boolean))].sort(),
    [jobs]
  );
  const allSkills = useMemo(() => {
    const set = new Set();
    jobs.forEach(j => (j.skills || '').split(',').forEach(s => { const t = s.trim(); if (t) set.add(t); }));
    return [...set].sort();
  }, [jobs]);
  const jobTypes = ['Full Time', 'Part Time', 'Contract', 'Internship', 'Freelance'];

  const filtered = useMemo(() => jobs.filter(job => {
    const term = filters.search.trim().toLowerCase();
    if (term && !((job.position_name || '').toLowerCase().includes(term)
      || (job.company_name || '').toLowerCase().includes(term)
      || (job.location || '').toLowerCase().includes(term))) return false;

    if (filters.location && !(job.location || '').toLowerCase().includes(filters.location.toLowerCase())) return false;

    if (filters.minSalary > 0 && (parseFloat(job.max_ctc) || parseFloat(job.min_ctc) || 0) < filters.minSalary) return false;

    if (filters.experience) {
      const expMin = parseInt(job.experience_min) || 0;
      const expMax = parseInt(job.experience_max) || expMin;
      const ranges = { '0-1': [0, 1], '1-3': [1, 3], '3-5': [3, 5], '5-10': [5, 10], '10+': [10, 99] };
      const [lo, hi] = ranges[filters.experience] || [0, 99];
      if (expMax < lo || expMin > hi) return false;
    }

    if (filters.jobTypes.length && !filters.jobTypes.includes(job.job_type || 'Full Time')) return false;

    if (filters.skills.length) {
      const jobSkills = (job.skills || '').toLowerCase();
      if (!filters.skills.every(s => jobSkills.includes(s.toLowerCase()))) return false;
    }
    return true;
  }), [jobs, filters]);

  const stats = {
    totalJobs: jobs.length,
    companies: new Set(jobs.map(j => j.company_name).filter(Boolean)).size,
    placements: '500+'
  };

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Header />

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-[#EEF3FF] via-[#F6F9FF] to-white border-b border-slate-200 overflow-hidden">
        <div className="absolute inset-0 opacity-40 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(10,77,255,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(16,185,129,0.05) 0%, transparent 50%)'
        }} />
        <div className="relative max-w-7xl mx-auto px-4 pt-12 pb-10 md:pt-16 md:pb-14">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-1.5 bg-blue-100 text-[#0A4DFF] text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              <Sparkles className="w-3.5 h-3.5" /> Skill-based Job Opportunities
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight tracking-tight">
              Find Your <span className="text-[#0A4DFF]">Dream Job</span>
            </h1>
            <p className="text-base md:text-lg text-slate-600 mt-4 max-w-2xl mx-auto">
              Connect with verified employers hiring across India. Get AI-matched roles based on your skills and certifications.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl shadow-blue-100/40 border border-slate-100 p-2 flex flex-col md:flex-row items-stretch gap-2">
            <div className="flex-1 flex items-center gap-2 px-3 border-b md:border-b-0 md:border-r border-slate-100">
              <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <Input
                value={filters.search}
                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                placeholder="Job title, skills or company"
                className="border-0 shadow-none focus-visible:ring-0 h-11 text-sm"
                data-testid="hero-search-input"
              />
            </div>
            <div className="flex items-center gap-2 px-3 border-b md:border-b-0 md:border-r border-slate-100">
              <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <select
                value={filters.location}
                onChange={(e) => setFilters(f => ({ ...f, location: e.target.value }))}
                className="bg-transparent border-0 focus:outline-none h-11 text-sm w-full md:w-36"
                data-testid="hero-location-select"
              >
                <option value="">All Locations</option>
                {locations.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2 px-3">
              <GraduationCap className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <select
                value={filters.experience}
                onChange={(e) => setFilters(f => ({ ...f, experience: e.target.value }))}
                className="bg-transparent border-0 focus:outline-none h-11 text-sm w-full md:w-32"
                data-testid="hero-experience-select"
              >
                <option value="">Any Exp</option>
                <option value="0-1">Fresher</option>
                <option value="1-3">1-3 yr</option>
                <option value="3-5">3-5 yr</option>
                <option value="5-10">5-10 yr</option>
                <option value="10+">10+ yr</option>
              </select>
            </div>
            <Button className="bg-[#0A4DFF] hover:bg-[#083ddb] text-white rounded-xl h-11 px-6 font-semibold" data-testid="hero-search-btn">
              <Search className="w-4 h-4 mr-1.5" /> Search
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 md:gap-6 max-w-4xl mx-auto mt-8">
            {[
              { icon: Briefcase, label: 'Jobs Available', value: stats.totalJobs, color: 'text-[#0A4DFF]', bg: 'bg-blue-50' },
              { icon: Building2, label: 'Companies Hiring', value: stats.companies, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { icon: Award, label: 'Students Placed', value: stats.placements, color: 'text-violet-600', bg: 'bg-violet-50' },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-slate-100 rounded-xl p-3 md:p-4 flex items-center gap-3 shadow-sm">
                <div className={`w-10 h-10 md:w-12 md:h-12 ${s.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                </div>
                <div>
                  <p className="text-xl md:text-2xl font-extrabold text-slate-900 leading-none">{s.value}</p>
                  <p className="text-[11px] md:text-xs text-slate-500 font-medium mt-1">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MAIN */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* AI Recommendations - only for students */}
        {isAuthenticated && user?.role === 'student' && recommendations.length > 0 && (
          <section className="mb-8" data-testid="ai-recommended-section">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-violet-600" /> Recommended for You
                </h2>
                <p className="text-sm text-slate-500 mt-0.5">Hand-picked jobs based on your skills & certifications</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {recommendations.slice(0, 3).map((rec, i) => (
                <JobCard
                  key={rec.job?.id || i}
                  job={rec.job}
                  matchScore={rec.match_score}
                  isRecommended
                  hasApplied={hasApplied(rec.job?.id)}
                  onApply={openApply}
                  onViewDetails={openDetails}
                />
              ))}
            </div>
          </section>
        )}

        {/* Mobile filter trigger */}
        <div className="lg:hidden mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">{filtered.length} Jobs Found</h2>
          <Button variant="outline" onClick={() => setMobileFiltersOpen(true)} className="rounded-lg" data-testid="mobile-filters-btn">
            <Filter className="w-4 h-4 mr-1.5" /> Filters
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Sidebar - desktop */}
          <div className="hidden lg:block">
            <FilterSidebar
              filters={filters}
              setFilters={setFilters}
              locations={locations}
              allSkills={allSkills}
              jobTypes={jobTypes}
              onClear={clearFilters}
            />
          </div>

          {/* Listings */}
          <div>
            <div className="hidden lg:flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">
                {filtered.length} {filtered.length === 1 ? 'Job' : 'Jobs'} Found
              </h2>
              {(filters.search || filters.location || filters.minSalary || filters.experience || filters.jobTypes.length || filters.skills.length) ? (
                <button onClick={clearFilters} className="text-sm text-[#0A4DFF] font-semibold hover:underline">
                  Reset Filters
                </button>
              ) : null}
            </div>

            {loading ? (
              <div className="text-center py-20">
                <div className="animate-spin h-12 w-12 border-4 border-[#0A4DFF] border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-slate-600">Loading jobs...</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center" data-testid="empty-state">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-10 h-10 text-slate-400" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">No jobs found</h3>
                <p className="text-slate-500 mb-5">Try changing filters or search terms</p>
                <Button onClick={clearFilters} className="bg-[#0A4DFF] hover:bg-[#083ddb] text-white rounded-lg">
                  Reset All Filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {filtered.map(job => (
                  <JobCard
                    key={job.id}
                    job={job}
                    hasApplied={hasApplied(job.id)}
                    onApply={openApply}
                    onViewDetails={openDetails}
                  />
                ))}
              </div>
            )}

            {/* My Applications quick link for students */}
            {isAuthenticated && user?.role === 'student' && myApplications.length > 0 && (
              <div className="mt-8 bg-gradient-to-r from-blue-50 to-violet-50 rounded-2xl p-5 flex items-center justify-between border border-blue-100">
                <div>
                  <h3 className="font-bold text-slate-900">Your Applications</h3>
                  <p className="text-sm text-slate-600 mt-0.5">You have applied to {myApplications.length} {myApplications.length === 1 ? 'job' : 'jobs'}</p>
                </div>
                <Button onClick={() => navigate('/student/dashboard')} variant="outline" className="rounded-lg border-[#0A4DFF] text-[#0A4DFF] hover:bg-[#0A4DFF] hover:text-white">
                  View Applications <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Mobile filter drawer */}
      <Dialog open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-4 lg:hidden">
          <DialogHeader>
            <DialogTitle>Filters</DialogTitle>
          </DialogHeader>
          <FilterSidebar
            filters={filters}
            setFilters={setFilters}
            locations={locations}
            allSkills={allSkills}
            jobTypes={jobTypes}
            onClear={clearFilters}
            mobile
          />
          <Button onClick={() => setMobileFiltersOpen(false)} className="w-full bg-[#0A4DFF] hover:bg-[#083ddb] text-white rounded-lg mt-4">
            Show {filtered.length} Jobs
          </Button>
        </DialogContent>
      </Dialog>

      {/* Job Details Modal */}
      <JobDetailsModal
        job={selectedJob}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        onApply={openApply}
        hasApplied={selectedJob ? hasApplied(selectedJob.id) : false}
      />

      {/* Apply Dialog (kept simple, with cover letter) */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-[#0A4DFF]" /> Apply for {selectedJob?.position_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
              <p className="font-bold text-slate-900">{selectedJob?.company_name}</p>
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-600">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{selectedJob?.location}</span>
                <span className="flex items-center gap-1"><IndianRupee className="w-3 h-3" />{formatSalary(selectedJob?.min_ctc, selectedJob?.max_ctc)}</span>
                <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{selectedJob?.job_type || 'Full Time'}</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Cover Letter <span className="text-slate-400 font-normal">(Optional)</span></label>
              <textarea
                value={coverLetter}
                onChange={(e) => setCoverLetter(e.target.value)}
                rows={5}
                placeholder="Tell the employer why you're a perfect fit for this role..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#0A4DFF] focus:ring-1 focus:ring-[#0A4DFF] focus:outline-none"
                data-testid="cover-letter-input"
              />
            </div>
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
              <p className="text-xs text-blue-700 flex items-start gap-1.5">
                <BadgeCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
                Your profile, skills and certifications will be shared with the employer
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setApplyOpen(false)} className="flex-1 rounded-lg">Cancel</Button>
              <Button
                onClick={submitApply}
                disabled={applying}
                className="flex-1 bg-[#0A4DFF] hover:bg-[#083ddb] text-white rounded-lg font-semibold"
                data-testid="confirm-apply-btn"
              >
                {applying ? 'Submitting...' : <>Submit Application <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
      <Chatbot />
    </div>
  );
};

export default JobPortal;
